// components/Sidebar.jsx
'use client'

import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import Link from 'next/link'
import {
    FaHome, FaKey,
    FaWallet, FaInfoCircle, FaBriefcase, FaBuilding, FaUsers, FaUsersCog,
    FaWpforms, FaImages, FaCalendarAlt, FaCog, FaPlus, FaChevronRight,
    FaChevronDown, FaGlobe, FaFilePdf, FaAddressBook, FaMapMarkerAlt,
    FaListAlt, FaPhone, FaGraduationCap, FaFolder, FaChartBar,
    FaUserShield, FaExternalLinkAlt, FaSpinner, FaFire,
    FaIdCard, FaExclamationTriangle, FaHelicopter, FaRoute, FaBroadcastTower,
    FaCrosshairs
} from 'react-icons/fa'
import { TbDrone } from 'react-icons/tb'
import { useAuth } from '@/contexts/AuthContext'
import { useMobileMenu } from '@/contexts/MobileMenuContext'
import { usePathname } from 'next/navigation'
import Image from 'next/image'
import { AppletTypes, getAppletHref } from '@/utils/appletRouting'
import { houseNavItemsFor } from '@/utils/houseNav'
import { isUasSpecialUser } from '@/utils/uas'
import {
    buildBuiltInApplets, isAccountType, isOrgAccountType,
} from '@/utils/builtInApplets'
import { isIntelUser } from '@/utils/intel'
import { buildPortalNavItems, buildIntelNavItems } from '@/utils/portalNav'
import { isSuperAdmin as isPortalSuperAdmin } from '@/utils/superAdmins'
import usePendingApprovals from '@/hooks/usePendingApprovals'
import usePendingMembershipRequests from '@/hooks/usePendingMembershipRequests'
import { logger } from '@/utils/log';
import { appletIconVersion } from '@/utils/appletIcon'
import toast from 'react-hot-toast'

// The App Access pending-approvals count — legacy's solid red pill
// (#dc3545), shown on the Approval Queue row and, when the folder is
// collapsed, on the folder row so the number is never hidden. The Org Admin
// row uses the same pill for unanswered membership requests.
function PendingBadge({ count }) {
    if (!count) return null
    return (
        <span className="ml-auto rounded-full bg-red-600 text-white text-[11px] font-bold leading-none px-2 py-1 whitespace-nowrap">
            {count}
        </span>
    )
}

// Applet status enum mapping — matches C# AppletStatusTypes: 0=Draft, 1=Beta, 2=Live
const AppletStatusTypes = {
    Draft: 0,
    Beta: 1,
    Live: 2
}

// Account type enum mapping
// Get status badge color and text
const getStatusBadge = (status) => {
    switch (status) {
        case AppletStatusTypes.Live:
            return { text: 'Live', bgColor: 'bg-green-600', textColor: 'text-white' }
        case AppletStatusTypes.Beta:
            return { text: 'Beta', bgColor: 'bg-yellow-500', textColor: 'text-gray-900' }
        case AppletStatusTypes.Draft:
        default:
            return { text: 'Draft', bgColor: 'bg-gray-500', textColor: 'text-white' }
    }
}

// Get default icon based on applet type
const getAppletIcon = (appletType) => {
    switch (appletType) {
        case AppletTypes.MultiItemList:
            return FaListAlt
        case AppletTypes.OpenSinglePDF:
            return FaFilePdf
        case AppletTypes.OpenSingleWebLink:
            return FaGlobe
        case AppletTypes.Directory:
            return FaAddressBook
        case AppletTypes.SimplePhoneBook:
            return FaPhone
        case AppletTypes.Training:
            return FaGraduationCap
        case AppletTypes.GeoDirectory:
            return FaMapMarkerAlt
        case AppletTypes.WebLinksList:
            return FaGlobe
        case AppletTypes.MultiVersionItemList:
            return FaListAlt
        default:
            return FaFolder
    }
}

// Base URL for applet icons
const ICON_BASE_URL = 'https://fireops.blob.core.windows.net/icons'

// Applet Icon component with proper fallback handling
const AppletIcon = ({ applet }) => {
    const [imageError, setImageError] = useState(false)
    // Use a stable cache buster that updates whenever the icon path changes.
    // This ensures the browser re-fetches the image after an icon upload
    // without causing unnecessary fetches on every render.
    // A re-uploaded custom icon keeps its path (<org>/<applet>.png), so the
    // path alone misses it; the icon's IconTimestamp changes on every upload.
    const [cacheBust, setCacheBust] = useState(() => Date.now())
    const iconKey = `${applet.icon}|${appletIconVersion(applet)}`
    const prevIconRef = React.useRef(iconKey)

    React.useEffect(() => {
        if (prevIconRef.current !== iconKey) {
            prevIconRef.current = iconKey
            setImageError(false)
            setCacheBust(Date.now())
        }
    }, [iconKey])

    if (!applet.icon || imageError) {
        const FallbackIcon = getAppletIcon(applet.appletType)
        return (
            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded flex items-center justify-center flex-shrink-0">
                <FallbackIcon className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
            </div>
        )
    }

    const iconUrl = `${ICON_BASE_URL}/${applet.icon}?r=${cacheBust}`

    return (
        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded flex items-center justify-center flex-shrink-0">
            <img
                src={iconUrl}
                alt=""
                className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                onError={() => setImageError(true)}
            />
        </div>
    )
}

// Built-in Applet Icon component
const BuiltInAppletIcon = ({ iconPath, fallbackIcon: FallbackIcon }) => {
    const [imageError, setImageError] = useState(false)

    if (imageError || !iconPath) {
        return FallbackIcon ? (
            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded flex items-center justify-center flex-shrink-0">
                <FallbackIcon className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
            </div>
        ) : null
    }

    const iconUrl = `${ICON_BASE_URL}/${iconPath}`

    return (
        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded flex items-center justify-center flex-shrink-0">
            <img
                src={iconUrl}
                alt=""
                className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                onError={() => setImageError(true)}
            />
        </div>
    )
}

// Platform indicator component
const PlatformIndicator = ({ type, title }) => {
    const imageMap = {
        firesync: '/firesync_cal_nav.png',
        fireops: '/firesync_ops_nav.png',
        ems: '/firesync_ems_nav.png'
    }

    const src = imageMap[type]
    if (!src) return null

    return (
        <Image
            src={src}
            alt={title}
            title={title}
            width={16}
            height={16}
            className="w-3 h-3 sm:w-4 sm:h-4 object-contain flex-shrink-0"
        />
    )
}

export default function Sidebar() {
    // Use shared mobile menu context instead of local state
    const { isMobileOpen, closeMobileMenu } = useMobileMenu()
    // The UAS nav folder starts COLLAPSED (owner request, 2026-08-07 — it is
    // seven items deep and pushed the rest of the sidebar off a phone screen).
    // This diverges from the legacy portal, which defaulted it open.
    //
    // The `false` has to be explicit. The renderer reads
    // `expandedFolders[key] !== false`, so a folder with no entry counts as
    // OPEN — dropping this key would leave the folder expanded, not collapse it.
    // 'app-access-nav' starts collapsed too — legacy's AppAccessFolderOpen
    // defaults to false in both menus.
    const [expandedFolders, setExpandedFolders] = useState({ 'uas-nav': false, 'app-access-nav': false })
    const [appletFilter, setAppletFilter] = useState('all')
    const {
        currentOrg,
        user,
        isAuthenticated,
        isModuleHidden,
        orgApplets,
        appletsLoading,
        authKey
    } = useAuth()
    const pathname = usePathname()

    // App Access pending-approvals count — refreshed on navigation/org
    // change inside the hook, never polled.
    const pendingApprovals = usePendingApprovals()
    // Org Admin count of unanswered membership requests — same rules.
    const pendingRequests = usePendingMembershipRequests()

    // Determine if user is admin
    const isAdmin = useMemo(() => {
        return currentOrg?.isAdmin || false
    }, [currentOrg])

    // Check if user is super admin. Still requires an org, as before.
    const isSuperAdmin = useMemo(
        () => !!currentOrg && isPortalSuperAdmin(user),
        [currentOrg, user]
    )

    // Toggle folder expansion
    const toggleFolder = useCallback((folderName) => {
        setExpandedFolders(prev => ({
            ...prev,
            [folderName]: !prev[folderName]
        }))
    }, [])

    // Close mobile menu on navigation
    const handleNavClick = useCallback(() => {
        closeMobileMenu()
    }, [closeMobileMenu])

    // Staffing Portal SSO navigation
    const [staffingLoading, setStaffingLoading] = useState(false)

    const navigateToStaffingPortal = useCallback(async () => {
        try {
            setStaffingLoading(true)
            const orgId = currentOrg?.OrgId || currentOrg?.orgId
            const response = await fetch('/api/auth/GenerateTempToken', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Key: authKey, OrgId: orgId })
            })
            // Legacy NavMenu.NavigateToStaffingAsync: three distinct failures,
            // each surfaced to the user (an HTTP error body is not JSON, so it
            // is checked before parsing).
            if (!response.ok) {
                logger.error('[SSO] GenerateTempToken HTTP error:', response.status)
                toast.error('Could not reach the auth service. Please try again.')
                return
            }
            const data = await response.json()
            const tempToken = data.TempToken || data.tempToken
            // A "success" with no token would open the staffing site with
            // ?tempToken=undefined — treat it as the same failure.
            if ((data.Success || data.success) && tempToken) {
                window.open(
                    `https://staffing.azurewebsites.net?tempToken=${encodeURIComponent(tempToken)}`,
                    '_blank',
                    'noopener,noreferrer'
                )
            } else {
                logger.error('[SSO] GenerateTempToken failed:', data.ErrorMessage || data.errorMessage)
                toast.error('Could not open Staffing Portal. Please try again.')
            }
        } catch (err) {
            logger.error('[SSO] navigateToStaffingPortal error:', err)
            toast.error('An unexpected error occurred. Please try again.')
        } finally {
            setStaffingLoading(false)
        }
    }, [authKey, currentOrg])

    // Group applets by folder
    const groupedApplets = useMemo(() => {
        if (!orgApplets || orgApplets.length === 0) return { unfiled: [], folders: {} }

        const folders = {}
        const unfiled = []

        const filteredApplets = orgApplets.filter(applet => {
            if (appletFilter === 'all') return true
            if (appletFilter === 'firesync') return applet.inFireSync
            if (appletFilter === 'fireops') return applet.inFireOps
            if (appletFilter === 'ems') return applet.inEms
            return true
        })

        filteredApplets.forEach(applet => {
            const folderName = applet.folderName
            if (folderName && folderName.trim()) {
                if (!folders[folderName]) {
                    folders[folderName] = []
                }
                folders[folderName].push(applet)
            } else {
                unfiled.push(applet)
            }
        })

        const sortApplets = (arr) => arr.sort((a, b) => {
            const seqA = a.sequenceNumber || 0
            const seqB = b.sequenceNumber || 0
            if (seqA !== seqB) return seqA - seqB
            return (a.name || '').localeCompare(b.name || '')
        })

        sortApplets(unfiled)
        Object.keys(folders).forEach(key => sortApplets(folders[key]))

        return { unfiled, folders }
    }, [orgApplets, appletFilter])

    // Navigation items live in utils/portalNav so the dashboard's shortcut
    // picker offers exactly what this menu shows — one builder, two consumers.
    const navigationItems = useMemo(
        () => buildPortalNavItems({ currentOrg, user, isAdmin, isSuperAdmin }),
        [currentOrg, isAdmin, isSuperAdmin, user])

    // ── Intelligence (Beta) ──
    // Its own section beneath the built-in applets, matching the legacy
    // NavMenu's "Intelligence" group (divider + labelled header carrying the
    // Beta badge, so the rows themselves don't repeat it).
    // Legacy gating: an eight-ID list for the section, org-admin for Admin.
    // That list is distinct from both the UAS gate and the portal super-admin
    // set — see utils/intel.js.
    const intelItems = useMemo(
        () => buildIntelNavItems({ currentOrg, user, isAdmin }),
        [currentOrg, isAdmin, user])

    // Built-in applets
    const builtInApplets = useMemo(
        () => buildBuiltInApplets({ currentOrg, isModuleHidden, isAdmin, user }),
        [currentOrg, isModuleHidden, isAdmin, user])

    // Render a single applet item
    const renderAppletItem = useCallback((applet, isInFolder = false) => {
        const statusBadge = getStatusBadge(applet.status)
        const href = getAppletHref(applet)
        const isActive = pathname === href || pathname === `/applet/${applet.id}`
        const appletKey = applet.id || `applet-${Math.random()}`

        return (
            <li key={appletKey}>
                <Link
                    href={href}
                    onClick={handleNavClick}
                    className={`
                        flex items-center gap-2 py-2 sm:py-2.5 transition-colors text-xs sm:text-sm
                        touch-target
                        ${isInFolder ? 'pl-6 sm:pl-8 pr-3 sm:pr-4' : 'px-3 sm:px-4'}
                        ${isActive
                        ? 'bg-gray-700 text-white border-l-4 border-yellow-500'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }
                    `}
                >
                    <span className={`text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded ${statusBadge.bgColor} ${statusBadge.textColor} font-medium min-w-[36px] sm:min-w-[40px] text-center`}>
                        {statusBadge.text}
                    </span>
                    <AppletIcon applet={applet} />
                    <span className="truncate flex-1">{applet.name?.replace(/_/g, ' ')}</span>
                    <span className="flex items-center gap-0.5 sm:gap-1 ml-1">
                        {applet.inFireSync && <PlatformIndicator type="firesync" title="FireSync" />}
                        {applet.inFireOps && <PlatformIndicator type="fireops" title="FireOps" />}
                        {applet.inEms && <PlatformIndicator type="ems" title="EMS" />}
                    </span>
                </Link>
            </li>
        )
    }, [pathname, handleNavClick])

    // Check if we should show applets section
    // GroupApplet accounts MUST see this section — browsing and adding org
    // applets is the entire purpose of that account type (legacy GroupMenu
    // renders the full folder + applet tree and the Add Applet button).
    const shouldShowAppletsSection = currentOrg &&
        !isAccountType(currentOrg, 'SharedTrainingCal') &&
        !isAccountType(currentOrg, 'SharedHouse') &&
        !isOrgAccountType(currentOrg, 'HouseOnly')

    // The "View Org Applets In" filter only makes sense once the org actually
    // has applets to filter. Deliberately keyed off the RAW list rather than
    // the grouped/filtered result: if it followed the filtered count, choosing
    // an app that matches nothing would hide the very control needed to switch
    // back. The Add Applet button is separate and still shows at zero, since
    // that is exactly when an admin needs it.
    const hasOrgApplets = (orgApplets?.length ?? 0) > 0

    return (
        <>
            {/* Mobile Overlay */}
            {isMobileOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-black/50 z-30 backdrop-blur-sm"
                    style={{ top: 'var(--header-height)' }}
                    onClick={closeMobileMenu}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
                    fixed left-0
                    w-[280px] sm:w-80 bg-gray-800 text-white 
                    flex flex-col shadow-lg z-40
                    transition-transform duration-300 ease-in-out overflow-hidden
                    lg:translate-x-0
                    sidebar-panel
                    ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
                `}
                style={{
                    // Height lives in `.sidebar-panel`, NOT here: it needs two
                    // declarations (vh fallback then dvh) and an inline style can
                    // only hold one value per property. Setting it here would also
                    // win over the class and silently reintroduce the bug.
                    top: 'var(--header-height)',
                }}
                role="navigation"
                aria-label="Main navigation"
            >
                {/* Navigation Links */}
                <nav className="flex-1 overflow-y-auto custom-scrollbar sidebar-scroll">
                    {/* Main Navigation */}
                    <ul className="py-1 sm:py-2">
                        {navigationItems.map((item, index) => {
                            if (item.type === 'divider') {
                                return (
                                    <li key={`divider-${index}`} className="px-3 sm:px-4 py-2">
                                        <div className="divider-accent" />
                                    </li>
                                )
                            }

                            // Collapsible folder in the MAIN nav (the App
                            // Access group) — same caret idiom as the UAS
                            // folder below, but with react-icons rows. The
                            // pending badge rides the folder only while it
                            // is collapsed; open, it sits on the queue row.
                            if (item.type === 'folder') {
                                const isOpen = expandedFolders[item.key] !== false
                                const FolderIcon = item.icon
                                return (
                                    <li key={`folder-${item.key}`}>
                                        <button
                                            onClick={() => toggleFolder(item.key)}
                                            aria-expanded={isOpen}
                                            className="w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5
                                                       text-gray-300 hover:bg-gray-700 hover:text-white
                                                       transition-colors text-sm touch-target"
                                        >
                                            <FolderIcon className="text-base sm:text-lg flex-shrink-0" />
                                            <span className="truncate">{item.label}</span>
                                            {isOpen
                                                ? <FaChevronDown className="text-[10px] text-gray-500 flex-shrink-0 -ml-1" />
                                                : <FaChevronRight className="text-[10px] text-gray-500 flex-shrink-0 -ml-1" />}
                                            {!isOpen && item.countBadge && (
                                                <PendingBadge count={pendingApprovals} />
                                            )}
                                        </button>
                                        {isOpen && (
                                            <ul className="bg-gray-900/30">
                                                {item.children.map((child) => {
                                                    const childActive = pathname === child.href
                                                    const ChildIcon = child.icon
                                                    return (
                                                        <li key={child.href}>
                                                            <Link
                                                                href={child.href}
                                                                onClick={handleNavClick}
                                                                className={`flex items-center gap-2 sm:gap-3 py-2 sm:py-2.5
                                                                            pl-8 sm:pl-10 pr-3 sm:pr-4
                                                                            transition-colors text-sm touch-target
                                                                            ${childActive
                                                                        ? 'bg-gray-700 text-white border-l-4 border-yellow-500'
                                                                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}
                                                            >
                                                                {/* Coloured on a white tile — the navImgBorder
                                                                    idiom, same as the UAS folder's children.
                                                                    Colours ride in from portalNav, legacy's
                                                                    verbatim. */}
                                                                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded flex items-center justify-center flex-shrink-0">
                                                                    <ChildIcon className="w-3 h-3 sm:w-4 sm:h-4"
                                                                               style={{ color: child.color || '#4b5563' }} />
                                                                </div>
                                                                <span className="truncate">{child.label}</span>
                                                                {child.countBadge && (
                                                                    <PendingBadge count={pendingApprovals} />
                                                                )}
                                                            </Link>
                                                        </li>
                                                    )
                                                })}
                                            </ul>
                                        )}
                                    </li>
                                )
                            }

                            const isActive = pathname === item.href
                            const Icon = item.icon

                            return (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        onClick={handleNavClick}
                                        className={`
                                            flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5 
                                            transition-colors text-sm touch-target
                                            ${isActive
                                            ? 'bg-gray-700 text-white border-l-4 border-yellow-500'
                                            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                        }
                                            ${item.special === 'back' ? 'text-orange-400 hover:text-orange-300' : ''}
                                        `}
                                    >
                                        {item.iconPath ? (
                                            <BuiltInAppletIcon iconPath={item.iconPath} fallbackIcon={Icon} />
                                        ) : (
                                            <Icon className="text-base sm:text-lg flex-shrink-0" />
                                        )}
                                        <span className="truncate">{item.label}</span>
                                        {item.badge && (
                                            <span className="badge badge-yellow ml-auto">
                                                {item.badge}
                                            </span>
                                        )}
                                        {item.requestsBadge && (
                                            <PendingBadge count={pendingRequests} />
                                        )}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>

                    {/* Built-In Applets Section */}
                    {builtInApplets.length > 0 && (
                        <>
                            <div className="px-3 sm:px-4 py-2">
                                <div className="divider-accent" />
                            </div>
                            <div className="px-3 sm:px-4 py-1 sm:py-2">
                                <h5 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                    Built-In Applets
                                </h5>
                            </div>
                            <ul>
                                {builtInApplets.map((item) => {
                                    const itemKey = item.id || item.href

                                    // Collapsible folder (the UAS group). The Beta
                                    // badge rides on the folder; children are
                                    // indented links. Uses the same applet icon
                                    // treatment as its siblings so the row lines up.
                                    if (item.type === 'folder') {
                                        const isOpen = expandedFolders[item.key] !== false
                                        return (
                                            <li key={`folder-${item.key}`}>
                                                <button
                                                    onClick={() => toggleFolder(item.key)}
                                                    aria-expanded={isOpen}
                                                    className="
                                                        w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5
                                                        text-gray-300 hover:bg-gray-700 hover:text-white
                                                        transition-colors text-sm touch-target
                                                    "
                                                >
                                                    {/* Icon first, caret AFTER the label: a leading caret
                                                        pushed this row's icon a column right of its
                                                        siblings and read as an indent (PM, 2 Sep 2026). */}
                                                    <BuiltInAppletIcon iconPath={item.iconPath} fallbackIcon={item.fallbackIcon || FaFolder} />
                                                    <span className="truncate">{item.label}</span>
                                                    {isOpen ? (
                                                        <FaChevronDown className="text-[10px] text-gray-500 flex-shrink-0 -ml-1" />
                                                    ) : (
                                                        <FaChevronRight className="text-[10px] text-gray-500 flex-shrink-0 -ml-1" />
                                                    )}
                                                    {item.badge && (
                                                        <span className="badge badge-yellow ml-auto">{item.badge}</span>
                                                    )}
                                                    {/* Which app the folder belongs to, same as every
                                                        other row. Legacy renders the OPS flame here from
                                                        the uas applet's InFireOps; the folder header was
                                                        the one place that never showed these. */}
                                                    <span className={`flex items-center gap-1 ${item.badge ? '' : 'ml-auto'}`}>
                                                        {item.inFireSync && <PlatformIndicator type="firesync" title="FireSync" />}
                                                        {item.inFireOps && <PlatformIndicator type="fireops" title="FireOps" />}
                                                        {item.inEms && <PlatformIndicator type="ems" title="EMS" />}
                                                    </span>
                                                </button>
                                                {isOpen && (
                                                    <ul className="bg-gray-900/30">
                                                        {item.children.map((child) => {
                                                            const childActive = pathname === child.href
                                                            const ChildIcon = child.icon
                                                            return (
                                                                <li key={child.href}>
                                                                    <Link
                                                                        href={child.href}
                                                                        onClick={handleNavClick}
                                                                        className={`
                                                                            flex items-center gap-2 sm:gap-3 py-2 sm:py-2.5
                                                                            pl-8 sm:pl-10 pr-3 sm:pr-4
                                                                            transition-colors text-sm touch-target
                                                                            ${childActive
                                                                            ? 'bg-gray-700 text-white border-l-4 border-yellow-500'
                                                                            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                                                        }
                                                                        `}
                                                                    >
                                                                        {/* Coloured on a white tile, matching legacy's
                                                                            navImgBorder. A bare glyph inherited the row's
                                                                            grey and read as disabled next to the applet
                                                                            icons above it. Colours are legacy's verbatim. */}
                                                                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded flex items-center justify-center flex-shrink-0">
                                                                            <ChildIcon className="w-3 h-3 sm:w-4 sm:h-4"
                                                                                       style={{ color: child.color || '#4b5563' }} />
                                                                        </div>
                                                                        <span className="truncate">{child.label}</span>
                                                                    </Link>
                                                                </li>
                                                            )
                                                        })}
                                                    </ul>
                                                )}
                                            </li>
                                        )
                                    }

                                    // External SSO items render as buttons, not Links
                                    if (item.isExternalSSO) {
                                        return (
                                            <li key={itemKey}>
                                                <button
                                                    onClick={() => { handleNavClick(); navigateToStaffingPortal() }}
                                                    disabled={staffingLoading}
                                                    className="
                                                        w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5
                                                        transition-colors text-sm touch-target text-left
                                                        text-gray-300 hover:bg-gray-700 hover:text-white
                                                        disabled:opacity-60
                                                    "
                                                >
                                                    {staffingLoading ? (
                                                        <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center flex-shrink-0">
                                                            <FaSpinner className="animate-spin text-gray-400 w-4 h-4" />
                                                        </div>
                                                    ) : (
                                                        <BuiltInAppletIcon iconPath={item.iconPath} fallbackIcon={FaFolder} />
                                                    )}
                                                    <span className="truncate">{item.label}</span>
                                                    <FaExternalLinkAlt className="ml-auto text-gray-500 text-xs flex-shrink-0" />
                                                </button>
                                            </li>
                                        )
                                    }

                                    const isActive = pathname === item.href

                                    return (
                                        <li key={itemKey}>
                                            <Link
                                                href={item.href}
                                                onClick={handleNavClick}
                                                className={`
                                                    flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5 
                                                    transition-colors text-sm touch-target
                                                    ${isActive
                                                    ? 'bg-gray-700 text-white border-l-4 border-yellow-500'
                                                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                                }
                                                `}
                                            >
                                                <BuiltInAppletIcon iconPath={item.iconPath} fallbackIcon={FaFolder} />
                                                <span className="truncate">{item.label}</span>
                                                <span className="flex items-center gap-0.5 sm:gap-1 ml-auto">
                                                    {item.inFireSync && <PlatformIndicator type="firesync" title="FireSync" />}
                                                    {item.inFireOps && <PlatformIndicator type="fireops" title="FireOps" />}
                                                    {item.inEms && <PlatformIndicator type="ems" title="EMS" />}
                                                </span>
                                            </Link>
                                        </li>
                                    )
                                })}
                            </ul>
                        </>
                    )}

                    {/* Intelligence Section */}
                    {intelItems.length > 0 && (
                        <>
                            <div className="px-3 sm:px-4 py-2">
                                <div className="divider-accent" />
                            </div>
                            <div className="px-3 sm:px-4 py-1 sm:py-2 flex items-center gap-2">
                                <h5 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                    Intelligence
                                </h5>
                                {/* ml-auto matches the UAS folder header and the
                                    nav items, so every Beta badge lines up on the
                                    same right edge. */}
                                <span className="badge badge-yellow ml-auto">Beta</span>
                            </div>
                            <ul>
                                {intelItems.map((item) => {
                                    const isActive = pathname === item.href
                                    const Icon = item.icon
                                    return (
                                        <li key={item.href}>
                                            <Link
                                                href={item.href}
                                                onClick={handleNavClick}
                                                className={`
                                                    flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5
                                                    transition-colors text-sm touch-target
                                                    ${isActive
                                                    ? 'bg-gray-700 text-white border-l-4 border-yellow-500'
                                                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                                }
                                                `}
                                            >
                                                <Icon className="text-base sm:text-lg flex-shrink-0" />
                                                <span className="truncate">{item.label}</span>
                                            </Link>
                                        </li>
                                    )
                                })}
                            </ul>
                        </>
                    )}

                    {/* Org Applets Section */}
                    {shouldShowAppletsSection && (
                        <>
                            <div className="px-3 sm:px-4 py-2 mt-2">
                                <div className="divider-accent" />
                            </div>

                            {/* Add Applet Button (Admin only) */}
                            {isAdmin && (
                                <div className="px-3 sm:px-4 py-1 sm:py-2">
                                    <Link
                                        href="/addapplet"
                                        onClick={handleNavClick}
                                        className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors touch-target"
                                    >
                                        <FaPlus className="text-base sm:text-lg" />
                                        <span className="font-medium text-sm">Add Applet</span>
                                    </Link>
                                </div>
                            )}

                            {/* Filter Dropdown — only when there is something to filter */}
                            {hasOrgApplets && (
                                <div className="px-3 sm:px-4 py-1 sm:py-2">
                                    <label className="text-xs text-gray-400 block mb-1">View Org Applets In:</label>
                                    <select
                                        value={appletFilter}
                                        onChange={(e) => setAppletFilter(e.target.value)}
                                        className="
                                            w-full bg-gray-700 text-white text-xs sm:text-sm rounded
                                            px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-600
                                            focus:outline-none focus:border-orange-500
                                            touch-target
                                        "
                                    >
                                        <option value="all">All</option>
                                        <option value="firesync">FireSync CAL</option>
                                        <option value="fireops">FireSync OPS</option>
                                        <option value="ems">FireSync EMS</option>
                                    </select>
                                </div>
                            )}

                            {/* Applets List — the spinner only while there is nothing to show;
                                a background refresh keeps the current list on screen. */}
                            {appletsLoading && !hasOrgApplets ? (
                                <div className="px-4 py-4 text-center">
                                    <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-2 border-orange-500 border-t-transparent mx-auto" />
                                    <p className="text-xs text-gray-400 mt-2">Loading applets...</p>
                                </div>
                            ) : (
                                <ul className="pb-4">
                                    {/* Folders */}
                                    {Object.keys(groupedApplets.folders).sort().map((folderName) => (
                                        <li key={folderName}>
                                            <button
                                                onClick={() => toggleFolder(folderName)}
                                                className="
                                                    w-full flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5
                                                    text-gray-300 hover:bg-gray-700 hover:text-white
                                                    transition-colors text-sm touch-target
                                                "
                                            >
                                                {expandedFolders[folderName] ? (
                                                    <FaChevronDown className="text-xs flex-shrink-0" />
                                                ) : (
                                                    <FaChevronRight className="text-xs flex-shrink-0" />
                                                )}
                                                <FaFolder className="text-yellow-500 flex-shrink-0" />
                                                <span className="truncate font-medium">{folderName}</span>
                                                <span className="text-xs text-gray-500 ml-auto">
                                                    ({groupedApplets.folders[folderName].length})
                                                </span>
                                            </button>
                                            {expandedFolders[folderName] && (
                                                <ul className="bg-gray-900/30">
                                                    {groupedApplets.folders[folderName].map(applet => renderAppletItem(applet, true))}
                                                </ul>
                                            )}
                                        </li>
                                    ))}

                                    {/* Unfiled Applets */}
                                    {groupedApplets.unfiled.map(applet => renderAppletItem(applet, false))}

                                    {/* Empty State */}
                                    {orgApplets.length === 0 && !appletsLoading && (
                                        <li className="px-4 py-4 text-center text-gray-400 text-sm">
                                            No applets found
                                        </li>
                                    )}
                                </ul>
                            )}
                        </>
                    )}
                </nav>
            </aside>
        </>
    )
}